import { useState, useEffect, useRef } from 'react';
import { Search, X, Tag, Package } from 'lucide-react';
import { PresetGroup } from '../lib/supabase';

interface GapRuleSelectorProps {
  tags: string[];
  groups: (PresetGroup & { presets: any[] })[];
  selectedTag?: string;
  selectedGroupId?: string;
  onChange: (tag: string | null, groupId: string | null) => void;
  label?: string;
  placeholder?: string;
}

export function GapRuleSelector({
  tags,
  groups,
  selectedTag = '',
  selectedGroupId = '',
  onChange,
  label = 'Select Tag or Group',
  placeholder = 'Search tags or groups...',
}: GapRuleSelectorProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getSelectedName = (): string => {
    if (selectedGroupId) {
      const group = groups.find((g) => g.id === selectedGroupId);
      return group ? `Group: ${group.name}` : '';
    }
    if (selectedTag) {
      return `Tag: ${selectedTag}`;
    }
    return '';
  };

  const handleSelect = (tag: string | null, groupId: string | null) => {
    onChange(tag, groupId);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange(null, null);
    setSearchTerm('');
    setIsOpen(false);
  };

  const filteredGroups = groups.filter((g) =>
    g.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredTags = tags.filter(
    (tag) => tag.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const hasSelection = selectedTag || selectedGroupId;
  const selectedName = getSelectedName();

  return (
    <div ref={containerRef} className="flex-1">
      <div className="flex items-center justify-between mb-1">
        <label className="block text-xs font-medium text-gray-700">{label}</label>
        {hasSelection && (
          <button
            onClick={handleClear}
            type="button"
            className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
          >
            <X size={12} />
            Clear
          </button>
        )}
      </div>

      {hasSelection && (
        <div className="mb-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-gray-900">
          <span className="font-medium">{selectedName}</span>
          {selectedGroupId && (
            <span className="text-xs text-gray-600 ml-2">
              ({groups.find((g) => g.id === selectedGroupId)?.presets.length || 0} supplements)
            </span>
          )}
        </div>
      )}

      <div className="relative mb-2">
        <Search
          size={14}
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
        />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {isOpen && (
        <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg max-h-64 overflow-y-auto shadow-lg">
          {filteredGroups.length === 0 && filteredTags.length === 0 ? (
            <div className="text-center py-6 text-gray-500 text-sm">
              {searchTerm ? 'No results found' : 'No tags or groups available'}
            </div>
          ) : (
            <>
              {filteredGroups.length > 0 && (
                <div>
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0">
                    Groups
                  </div>
                  <div className="divide-y divide-gray-100">
                    {filteredGroups.map((group) => {
                      const isSelected = group.id === selectedGroupId;
                      return (
                        <div
                          key={group.id}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleSelect(null, group.id);
                          }}
                          className={`w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors cursor-pointer ${
                            isSelected ? 'bg-blue-100' : ''
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <Package className="text-orange-600 flex-shrink-0" size={16} />
                            <div>
                              <div className="font-medium text-gray-900">{group.name}</div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {group.presets.length} supplement{group.presets.length !== 1 ? 's' : ''}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {filteredTags.length > 0 && (
                <div>
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 sticky top-0">
                    Tags
                  </div>
                  <div className="divide-y divide-gray-100">
                    {filteredTags.map((tag) => {
                      const isSelected = tag === selectedTag;
                      return (
                        <div
                          key={tag}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleSelect(tag, null);
                          }}
                          className={`w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors cursor-pointer ${
                            isSelected ? 'bg-blue-100' : ''
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <Tag className="text-blue-600 flex-shrink-0" size={16} />
                            <div className="font-medium text-gray-900">{tag}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
